import { Checkbox, PlusLink } from "@clarkinc/zest-design-system";
import { Icon, Tooltip } from "@clarkinc/zest-design-system/ui";
import { Image } from "@wss/image";
import { connect } from "react-redux";
import React, {
  ComponentProps,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { cls } from "@wss/cls";
import { Store } from "@wss/store";
import LimitedSaleStockNotice from "~/EventDayPage/LimitedSaleStockNotice";
import spritePath from "~/common/Icon/critical-symbol.svg";
import standardSpritePath from "~/common/Icons/Standard/standard-sprite.svg";
import zestSpritePath from "~/common/Icons/Zest/remixicon.symbol.v1.svg";
import { User } from "~/common/Store/connectUserAndChat";
import Stars from "../../common/Stars";
import {
  Product,
  ProductComparisonItem,
  SelectedProduct,
} from "../../common/Store/models/ProductListing";
import { RootState, UserState } from "../../common/Store/models/RootReadonly";
import { PurchasingGroupProps } from "../PurchasingGroupPage/purchasingGroupPageResult";
import AddToCartButton from "./AddToCart";
import GoodBetterBest from "./GoodBetterBestRibbon";
import PlatinumPlusExclusive from "./PlatinumPlusExclusive";
import Pricing from "./Pricing";
import getLowestPrice from "./getLowestPrice";
import getLowestPriceGroupMember from "./getLowestPriceGroupMember";
import isActiveSale from "./isActiveSale";
import isNonExclusiveForPlatinumPlusProduct from "./isNonExclusivePlatinumPlusProduct";
import setSelectedProductToGroupMember from "./setSelectedProduct";
import QuantityCountBadge from "./components/QuantityCountBadge";
import { sendGtmEvent } from "~/analytics/definitions";
import { CompareProductsState } from "../compareProductStore";
import BadgeAndOptionText from "./BadgeAndOptionText";
import { getQuantityCountBadgeData } from "./module";
import { useFeatureFlag } from "~/common/contexts/FeatureFlagProvider";
import Ribbon from "~/common/Ribbon/primitive";

function productBoxGetIsSelectedForCompare(
  product: Product,
  itemNumbers: string[],
  itemNumber: string,
): boolean {
  return (
    (!product.groupingAttributes.isVirtual &&
      itemNumbers.includes(itemNumber)) ||
    // if item is vg and any members are selected, keep it selected
    (product.groupingAttributes.isVirtual &&
      [
        product.groupingAttributes.virtualGroupItemNumber,
        ...product.groupingAttributes.groupMembers.map((gm) => gm.itemNumber),
      ].some((iNum) => itemNumbers.includes(iNum)))
  );
}

type ProductBoxGridViewWrapperProps = {
  product: Product;
  itemNumber: string;
  compareProductsStore?: Store<CompareProductsState>;
};

function ProductBoxGridViewWrapper({
  product,
  itemNumber,
  compareProductsStore,
  children,
}: PropsWithChildren<ProductBoxGridViewWrapperProps>): ReactElement {
  const state = compareProductsStore?.useState() ?? {
    active: false,
    items: [],
  };
  const compareItemNumbers = state.items.map((item) => item.itemNumber);

  const isSelected = state.active
    ? productBoxGetIsSelectedForCompare(product, compareItemNumbers, itemNumber)
    : false;
  return (
    <span
      className={cls(
        "group border-transparent border-solid border-6 m-0 max-w-full relative",
        isSelected && "shadow hover:shadow-blue",
      )}
    >
      {children}
    </span>
  );
}

type ProductBoxCompareProductsProps = {
  product: Product;
  itemNumber: string;
  descriptionHtml: string;
  primaryImagePath: string;
  link: string;
  productListingView?: "list" | "grid";
  compareProductsStore: Store<CompareProductsState>;
  handleCompareErrorModalOpen?: () => void;
};

function ProductBoxCompareProducts({
  product,
  itemNumber,
  descriptionHtml,
  link,
  primaryImagePath,
  productListingView,
  compareProductsStore,
  handleCompareErrorModalOpen,
}: ProductBoxCompareProductsProps): ReactElement | null {
  const compareProducts = compareProductsStore.useState();
  const compareItemToAdd: ProductComparisonItem = {
    itemNumber,
    itemTitle: descriptionHtml,
    itemImage: primaryImagePath,
    itemLink: link,
  };

  const compareItemNumbers = compareProducts.items.map(
    (item) => item.itemNumber,
  );
  const isSelectedForCompare = productBoxGetIsSelectedForCompare(
    product,
    compareItemNumbers,
    itemNumber,
  );

  const handleCompareCheckboxClick: React.ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    const vgItemNumbers = [
      product.groupingAttributes.virtualGroupItemNumber,
      ...product.groupingAttributes.groupMembers.map((gm) => gm.itemNumber),
    ].filter((gm) => gm);

    const currentVgItemIndex = compareItemNumbers.findIndex((iNum) =>
      vgItemNumbers.includes(iNum),
    );
    const currentItemIndex = (compareProducts.items ?? []).findIndex((item) =>
      vgItemNumbers.includes(item.itemNumber),
    );
    const itemNumbers = [...compareItemNumbers];
    const items = [...(compareProducts.items ?? [])];

    if (
      !isSelectedForCompare &&
      compareItemNumbers.length >= 4 &&
      handleCompareErrorModalOpen
    ) {
      event.preventDefault();
      handleCompareErrorModalOpen();
      return;
    }

    if (!isSelectedForCompare) {
      if (currentVgItemIndex >= 0) {
        itemNumbers[currentVgItemIndex] = itemNumber;
      } else {
        itemNumbers.push(itemNumber);
      }
      if (currentItemIndex >= 0) {
        items[currentItemIndex] = compareItemToAdd;
      } else {
        items.push(compareItemToAdd);
      }

      compareProductsStore?.setState({ items });
    } else {
      compareProductsStore?.setState({
        items: items.filter(
          (item) =>
            item.itemNumber !== itemNumber &&
            !vgItemNumbers.includes(item.itemNumber),
        ),
      });
    }
  };

  useEffect(
    function itemNumberChangedEffect() {
      if (!compareProducts.active) {
        return;
      }

      const vgItemNumbers = [
        product.groupingAttributes.virtualGroupItemNumber,
        ...product.groupingAttributes.groupMembers.map((gm) => gm.itemNumber),
      ].filter((gm) => !!gm);

      if (vgItemNumbers.some((item) => compareItemNumbers.includes(item))) {
        const currentItemIndex = (compareProducts.items ?? []).findIndex(
          (item) => vgItemNumbers.includes(item.itemNumber),
        );

        const items = [...(compareProducts.items ?? [])];

        items[currentItemIndex] = compareItemToAdd;

        compareProductsStore?.setState({
          items,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemNumber],
  );

  return compareProducts.active ? (
    <div
      className={cls(
        "hidden md:block",
        productListingView === "list" &&
          "hidden md:table-cell relative w-12 h-full align-middle",
      )}
    >
      <div
        className={`w-6 h-6 z-20 border-solid border-[3px] rounded-md border-gray-100
                    ${isSelectedForCompare && "border-white"} ${
                      productListingView === "list"
                        ? "relative ml-4 mr-1.5"
                        : "absolute right-4 top-4"
                    }`}
        data-testid="product-comparison-checkbox-wrapper"
      >
        <Checkbox
          id="product-comparison-checkbox"
          data-testid="product-comparison-checkbox"
          aria-label={
            isSelectedForCompare
              ? "Deselect product for comparison"
              : "Select product for comparison"
          }
          aria-checked={isSelectedForCompare}
          label=""
          theme="wss"
          onChange={handleCompareCheckboxClick}
          checked={isSelectedForCompare}
          className="w-6 h-6 drop-shadow"
        />
      </div>
    </div>
  ) : null;
}

function ProductBoxPrimaryImage({
  compareProductsStore,
  product,
  itemNumber,
  ...rest
}: ProductBoxGridViewWrapperProps & ComponentProps<"img">): ReactElement {
  const state = compareProductsStore?.useState() ?? {
    active: false,
    items: [],
  };
  const compareItemNumbers = state.items.map((item) => item.itemNumber);

  const isSelected = state.active
    ? productBoxGetIsSelectedForCompare(product, compareItemNumbers, itemNumber)
    : false;

  return (
    <Image
      {...rest}
      data-testid="productBoxImage"
      width={1}
      height={1}
      className={`h-24 max-w-none m-2 w-24 xs:h-20 xs:w-20 lz:h-32 lz:w-32 border-2 border-solid border-white rounded-md ${
        isSelected ? "outline-blue-500 shadow-blue" : "outline-none"
      }`}
    />
  );
}

function ProductBoxSecondaryImage({
  compareProductsStore,
  product,
  itemNumber,
  ...rest
}: ProductBoxGridViewWrapperProps & ComponentProps<"img">): ReactElement {
  const state = compareProductsStore?.useState() ?? {
    active: false,
    items: [],
  };
  const compareItemNumbers = state.items.map((item) => item.itemNumber);

  const isSelected = state.active
    ? productBoxGetIsSelectedForCompare(product, compareItemNumbers, itemNumber)
    : false;

  return (
    <Image
      {...rest}
      data-testid="productBoxImage"
      width={1}
      height={1}
      loading="lazy"
      fetchpriority="low"
      className={cls(
        "h-24 max-w-none opacity-0 m-2 absolute inset-0 w-24 transition ease-in-out duration-300 xs:h-20 xs:w-20 lz:h-32 lz:hover:opacity-100 lz:w-32 border-2 border-solid border-white rounded-md",
        isSelected ? "shadow-blue" : "outline-none",
      )}
    />
  );
}

type ProductListingView = {
  productListingView?: "list" | "grid";
};

type ContainerProps = {
  itemNumber: string;
  children: React.ReactNode;
  groupMemberItemNumbers?: string[];
  previouslyPurchased?: string;
  defaultTrackedGtmItemNumber?: string;
  isQuantityDiscountPopoverActive?: boolean;
  isOnCompareProductsPage?: boolean;
  compareProductsStore?: Store<CompareProductsState>;
  product: Product;
} & ProductListingView;

export const Container = ({
  compareProductsStore,
  itemNumber,
  children,
  groupMemberItemNumbers = [],
  productListingView,
  previouslyPurchased = undefined,
  defaultTrackedGtmItemNumber,
  isOnCompareProductsPage = false,
  product,
}: ContainerProps) => {
  const state = compareProductsStore?.useState() ?? {
    active: false,
    items: [],
  };
  const compareItemNumbers = state.items.map((item) => item.itemNumber);

  const isSelectedForCompare = state.active
    ? productBoxGetIsSelectedForCompare(product, compareItemNumbers, itemNumber)
    : false;

  let backgroundColor;

  if (isSelectedForCompare) {
    if (productListingView === "grid") {
      backgroundColor =
        "bg-white/100 md:bg-white/10 md:bg-gradient-to-b md:from-transparent md:to-white";
    } else {
      backgroundColor =
        "bg-white/100 md:bg-white/10 md:bg-gradient-to-r md:from-transparent md:to-white";
    }
  } else {
    backgroundColor = "bg-white";
  }
  const prevPurchasedStyles = previouslyPurchased ? "pt-4" : "";
  const compareStyles = isOnCompareProductsPage
    ? "py-4 px-10 min-w-full"
    : "border-solid xsl:p-4";

  const productBoxStyles =
    productListingView === "grid"
      ? `border-gray-200 border border-t-0 border-l-0 box-border flex flex-col p-2 relative ${prevPurchasedStyles} ${compareStyles}`
      : "border-gray-200 border-solid border-0 border-b last:border-b-0 block m-0 first:mt-px pr-2 pb-2 relative xs:table xs:p-0 xs:w-full";

  return (
    <div
      className={`product-box-container
      ${
        previouslyPurchased
          ? "bg-[linear-gradient(180deg,#daeffa_18%,#fff_62%)] opacity-100 transition-all duration-300 ease-in"
          : backgroundColor
      }
      ${productBoxStyles}
    `}
      data-item-number={itemNumber}
      data-group-members={groupMemberItemNumbers}
      id="ProductBoxContainer"
      data-testid="productBoxContainer"
      gtm-tracking-number={defaultTrackedGtmItemNumber}
    >
      {children}
    </div>
  );
};

type ItemNumberProps = {
  itemsOnAutoReorder?: Readonly<string[]>;
  product: Product;
  user: User;
  selectedProduct: SelectedProduct;
  userHasSelectedGroupMember: boolean;
} & ProductListingView;

const ItemNumber = ({
  productListingView,
  selectedProduct,
  product,
  user,
  itemsOnAutoReorder = [],
  userHasSelectedGroupMember,
}: ItemNumberProps) => {
  const { itemNumber } = userHasSelectedGroupMember ? selectedProduct : product;

  const calculateIsActiveAutoReorder = () => {
    return (
      itemsOnAutoReorder.includes(product.itemNumber) ||
      itemsOnAutoReorder.some(
        (autoReorderItem) =>
          product.groupingAttributes.groupMembers
            .map((member) => member.itemNumber)
            .indexOf(autoReorderItem) >= 0,
      )
    );
  };

  const tooltipContent = calculateIsActiveAutoReorder()
    ? "You have this item on Auto Reorder"
    : "Auto Reorder eligible";

  return (
    <div
      className={`${
        productListingView === "list" ? "hidden md:flex items-center mb-1" : ""
      }`}
    >
      <p
        className={
          productListingView === "list"
            ? "flex leading-4 uppercase mb-0"
            : "border-gray-200 border-solid border-0 border-b leading-4 my-0 relative uppercase"
        }
        style={{
          color: productListingView === "list" ? "#484a47" : "#aeb0ad",
          fontSize: "10px",
        }}
        data-testid="itemNumber"
      >
        <span
          className={
            productListingView === "list"
              ? "pr-2"
              : "whitespace-nowrap bg-white py-0 px-1 absolute text-gray-800"
          }
          style={
            productListingView === "list"
              ? {}
              : { top: "-8px", left: "50%", transform: "translateX(-50%)" }
          }
        >
          <span className="sr-only">Item number</span>
          <span aria-hidden="true">#</span>
          <span>
            {selectedProduct.isOutlet ||
            (product.groupingAttributes.isVirtual &&
              !userHasSelectedGroupMember)
              ? product.originalId
              : itemNumber}
          </span>
        </span>
      </p>
      {user.registered && product.isAutomaticReorderEligible && (
        <Tooltip className="text-sm ml-2" content={tooltipContent}>
          <button
            data-testid={tooltipContent}
            type="button"
            className={`appearance-none border-none p-0 m-0 h-4 focus:outline-none ${
              productListingView === "grid"
                ? "absolute right-0 bg-white pl-2 pr-2 -mt-2"
                : "bg-transparent inline "
            }`}
          >
            <Icon
              name="fastforward-clock"
              spriteSheetHref={standardSpritePath}
              size="xsmall"
              className={cls(
                calculateIsActiveAutoReorder()
                  ? "stroke-orange-500"
                  : "stroke-gray-500",
              )}
            />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

type TitleProps = {
  descriptionHtml: string;
  link: string;
  oneToFiveStarRating: number;
  hasVariationLabel: boolean;
  hasVirtualGrouping?: boolean;
  showBadge?: boolean;
} & ProductListingView;

const Title = ({
  productListingView,
  descriptionHtml,
  link,
  hasVariationLabel,
  hasVirtualGrouping,
  showBadge,
}: TitleProps) => {
  let marginTop;

  if (productListingView === "grid") {
    marginTop =
      hasVariationLabel || hasVirtualGrouping || showBadge ? "mt-0" : "mt-6";
  } else {
    marginTop =
      hasVariationLabel || hasVirtualGrouping || showBadge ? "mt-0" : "mt-2";
  }

  return productListingView === "grid" ? (
    <span
      className={`block font-semibold leading-none ${
        productListingView === "grid"
          ? `text-sm-1/2 mb-0 max-h-10 hover:max-h-full min-h-10 overflow-hidden hover:overflow-visible text-center ${marginTop}`
          : "text-sm my-2 xxxs:leading-4 xs:mt-0 xs:mb-1 lz:text-base lz:leading-none lz:max-w-lg"
      }`}
      data-testid="itemDescription"
    >
      {descriptionHtml}
    </span>
  ) : (
    <a
      href={link}
      className={`block font-semibold text-sm leading-none mb-2 xxxs:leading-4 xs:mb-1 lz:text-base lz:leading-none lz:max-w-lg ${marginTop}`}
      data-testid="itemDescription"
    >
      {descriptionHtml}
    </a>
  );
};

const productBoxVariationsOnClick = (
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  showBadge: boolean,
  link: string,
) => {
  const target = event.target;
  let clickText = "";

  if (target instanceof Element) {
    const badgeSpan = target.closest(".badgeSpan");

    if (badgeSpan == null) {
      return;
    }
    const optionText =
      event.currentTarget.querySelector('[data-testid="option-text"]')
        ?.textContent ?? "";
    const badgeText =
      event.currentTarget.querySelector(".badgeText")?.textContent ?? "";

    if (showBadge) {
      clickText += badgeText;
    }
    if (badgeText != "" && optionText != "") {
      clickText += " ";
    }
    if (optionText != "") {
      clickText += optionText;
    }
  }

  sendGtmEvent({
    event: "ga4_event",
    eventName: "PLP Variations",
    eventAction: `PLP Variations Click`,
    eventLabel: `${link}:${clickText}`,
    eventCategory: window.location.pathname,
  });
};

interface Props extends ProductListingView {
  product: Product;
  mounted: boolean;
  user: UserState;
  lazyLoadImages: boolean;
  compareProductsStore?: Store<CompareProductsState>;
  hasCustomizableProductsOverride?: boolean;
  purchasingGroupProps?: PurchasingGroupProps;
  itemsOnAutoReorder?: Readonly<string[]>;
  isItemAlreadyInCart?: boolean;
  index?: number;
  discountCode?: string;
  showSaleStockNotice?: boolean;
  shouldPreselectVirtualGrouping?: boolean;
  facetsWithMultipleSelections?: string[];
  previouslyPurchased?: string;
  setIsQuantityDiscountPopoverOpen?: React.Dispatch<
    React.SetStateAction<string>
  >;
  isQuantityDiscountPopoverOpen?: string;
  isOnCompareProductsPage?: boolean;
  handleCompareErrorModalOpen?: () => void;
}

const ProductBox = ({
  product,
  user,
  mounted,
  lazyLoadImages,
  compareProductsStore,
  productListingView,
  hasCustomizableProductsOverride = false,
  purchasingGroupProps,
  itemsOnAutoReorder = [],
  isItemAlreadyInCart = false,
  discountCode,
  showSaleStockNotice = false,
  shouldPreselectVirtualGrouping,
  facetsWithMultipleSelections,
  previouslyPurchased = "",
  setIsQuantityDiscountPopoverOpen,
  isQuantityDiscountPopoverOpen = "",
  isOnCompareProductsPage = false,
  handleCompareErrorModalOpen,
}: Props) => {
  const {
    altHtml,
    hasBadge,
    badgeImagePath,
    badgeDisplayText,
    customerExclusivity,
    isRequestAQuoteEnabled,
    qualityRatingMembership,
    oneToFiveStarRating,
    overallRating,
    variationMembership,
  } = product;

  const { evaluateFlag } = useFeatureFlag();

  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct>({
    ...product,
    activeSale: product.price.displaySalePrice,
    isOutlet: product.outletAttributes && product.outletAttributes.isOutlet,
    price: {
      ...product.price,
      originalPrice:
        product.outletAttributes && product.outletAttributes.originalPrice,
    },
  });

  const [userHasSelectedGroupMember, setUserHasSelectedGroupMember] =
    useState<boolean>(false);

  const [preselectedVgMemberItemNumber, setPreselectedVgMemberItemNumber] =
    useState("");

  const {
    link,
    primaryImagePath,
    secondaryImagePath,
    descriptionHtml,
    isWebstaurantPlusEligible,
    isNewProduct,
    itemNumber,
    hasFreeShipping,
  } = userHasSelectedGroupMember ? selectedProduct : product;

  const vgCategory =
    product.groupingAttributes.groupMembers[0]?.configurationOptions[0]?.name;

  let hasMultipleVgFacets = false;

  facetsWithMultipleSelections?.forEach((facet) => {
    if (
      vgCategory?.toLowerCase().replace(/\s+/g, "-") === facet?.toLowerCase()
    ) {
      hasMultipleVgFacets = true;
    }
  });

  /**
   * We may cache the HTML in a key value store or on a CDN so we'll double check if
   * the sale is active on the client.
   */
  useEffect(() => {
    const { groupMembers } = product.groupingAttributes;

    if (groupMembers === undefined || groupMembers.length === 0) {
      setSelectedProduct({
        ...selectedProduct,
        activeSale: isActiveSale(selectedProduct.price),
      });
    } else if (
      !hasMultipleVgFacets &&
      shouldPreselectVirtualGrouping &&
      product.groupingAttributes.isVirtual &&
      groupMembers.some((x) => x.itemNumber === product.itemNumber)
    ) {
      const groupMember = groupMembers.find(
        (x) => x.itemNumber === product.itemNumber,
      );
      if (groupMember) {
        setPreselectedVgMemberItemNumber(groupMember.itemNumber);
        setSelectedProductToGroupMember(
          product,
          groupMember,
          setSelectedProduct,
        );
      }
    } else {
      const defaultGroupMember = {
        ...getLowestPriceGroupMember(groupMembers, user, product.originalId)!,
      };

      const defaultIsInStock = groupMembers.some((member) => member.isInStock);

      /**
       * The isInStock override for the function below is provided so that the default isInStock value for the main ProductBox tile comes from there
       * being at least one VG item in stock
       */
      setSelectedProductToGroupMember(
        product,
        defaultGroupMember,
        setSelectedProduct,
        defaultIsInStock,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.index]);

  const isSalePriceTheLowest = (() => {
    if (selectedProduct.price.salePrice <= 0) {
      return false;
    }

    const lowestPrice = getLowestPrice(
      selectedProduct.price,
      user,
      selectedProduct.activeSale,
      selectedProduct.showBelowMinimumAdvertisedPriceForLoggedInCustomers,
      selectedProduct.showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    );

    return lowestPrice === selectedProduct.price.salePrice;
  })();

  const isNewProductOrQualityRating = isNewProduct || qualityRatingMembership;

  const showSaleRibbon = selectedProduct.activeSale && isSalePriceTheLowest;

  /**
   * Determines whether a margin bottom style should be added to a ribbon in grid view.
   * @remarks
   *
   *  Order of visible ribbons in grid view: New, Quality Rating, Sale.
   *
   *  Returns true if New product ribbon exists and either Sale or Quality Rating exists,
   *  OR If Quality rating ribbon exists and Sale ribbon exists.
   *
   */
  const moreThanOneRibbon =
    (isNewProduct && !!(showSaleRibbon || qualityRatingMembership)) ||
    !!(qualityRatingMembership && showSaleRibbon);

  const showRibbonStack =
    isWebstaurantPlusEligible ||
    isNewProductOrQualityRating ||
    showSaleRibbon ||
    oneToFiveStarRating > 0;

  const groupMemberItemNumbers = product.groupingAttributes?.groupMembers?.map(
    (p) => p.itemNumber,
  );

  const defaultTrackedGtmItemNumber =
    selectedProduct.isOutlet ||
    (product.groupingAttributes.isVirtual && !userHasSelectedGroupMember)
      ? product.originalId
      : itemNumber;

  const isQuantityDiscountPopoverActive =
    isQuantityDiscountPopoverOpen === itemNumber ||
    (!!groupMemberItemNumbers.length &&
      groupMemberItemNumbers.includes(isQuantityDiscountPopoverOpen));

  const shouldShowGbbRibbon =
    qualityRatingMembership &&
    qualityRatingMembership.groupItemNumbers?.length > 0;

  const gridQuoteStyles = isRequestAQuoteEnabled
    ? "pt-5 xs:pt-7 xs:pb-2 leading-[13px]"
    : "pt-4";
  const listQuoteStyles = isRequestAQuoteEnabled ? "leading-[13px]" : "";

  const pricingStyles =
    productListingView === "grid"
      ? `flex flex-col justify-center flex-auto text-center xs:min-h-11 ${gridQuoteStyles}`
      : `block text-left w-auto xs:table-cell xs:align-middle xs:text-center xs:w-3/12 ${listQuoteStyles}`;

  const showBadge = hasBadge && badgeDisplayText?.length > 0;

  const badgeData = getQuantityCountBadgeData(
    selectedProduct.unitsPerPackaging ?? 0,
    selectedProduct.productBadges ?? [],
    !!product.groupingAttributes?.isGroupMaster && !userHasSelectedGroupMember,
    product.groupingAttributes.groupMembers,
    selectedProduct.unitOfMeasure,
  );

  const productBoxAnalyticsOnClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    productBoxVariationsOnClick(event, showBadge, link);

    if (!badgeData) return;
    sendGtmEvent({
      event: "ga4_event",
      eventName: "PLP Quantity Badge Tracking",
      eventAction: `PLP Product Click ${product.itemNumber}`,
      eventLabel: "Quantity Badge Visible",
      eventCategory: window.location.href,
    });
  };

  const accountVariationsForOptionText = evaluateFlag(
    "collapsible-variation-toggle",
  );

  const hasVariations =
    accountVariationsForOptionText &&
    (variationMembership?.hasMembership ?? false);

  const hasVariationLabel =
    hasVariations &&
    ((variationMembership?.variationGroups?.filter((x) => x.isCollapsed)
      .length ?? 0) > 0 ||
      (variationMembership?.variationGroups?.filter(
        (x) => x.optionName === "Color",
      ).length ?? 0) > 0);

  return (
    <Container
      {...{
        isSelectedForCompare: false,
        itemNumber,
        groupMemberItemNumbers,
        productListingView,
        previouslyPurchased,
        defaultTrackedGtmItemNumber,
        isQuantityDiscountPopoverActive,
        isOnCompareProductsPage,
        compareProductsStore,
        product,
      }}
    >
      <>
        {compareProductsStore ? (
          <ProductBoxCompareProducts
            product={product}
            itemNumber={itemNumber}
            descriptionHtml={descriptionHtml}
            link={link}
            compareProductsStore={compareProductsStore}
            primaryImagePath={primaryImagePath}
            productListingView={productListingView}
            handleCompareErrorModalOpen={handleCompareErrorModalOpen}
          />
        ) : null}
        {productListingView === "grid" ? (
          <>
            <ProductBoxGridViewWrapper
              product={product}
              itemNumber={itemNumber}
              compareProductsStore={compareProductsStore}
            >
              <a
                className="block"
                onClick={productBoxAnalyticsOnClick}
                href={link}
                data-testid="itemLink"
              >
                <p
                  className={`absolute flex justify-center gap-1 items-center top-[-18px] text-blue-900 text-xs whitespace-nowrap font-semibold w-full text-center transition-opacity ease-in
                ${previouslyPurchased ? "opacity-100" : "opacity-0"}
                `}
                  aria-hidden={!previouslyPurchased}
                >
                  <Icon
                    name="ri-time-line"
                    size="2xsmall"
                    spriteSheetHref={zestSpritePath}
                    theme="wss"
                    className="min-w-[0.75rem]"
                  />
                  Last Purchased {previouslyPurchased}
                </p>
                <span
                  className={`block h-full m-0 relative border-solid border-white border-2 rounded-md ${
                    isOnCompareProductsPage ? "max-w-[120px] mx-auto" : ""
                  }`}
                >
                  {badgeData && (
                    <QuantityCountBadge
                      unitDisplayText={badgeData.unitOfMeasure}
                      quantity={badgeData.unitsPerPackaging ?? 1}
                      isGridView
                    />
                  )}
                  <Image
                    alt=""
                    src={primaryImagePath}
                    width={1}
                    height={1}
                    loading={lazyLoadImages ? "lazy" : "eager"}
                    data-testid="productBoxImage"
                    className="max-w-full inset-0 w-full rounded h-auto"
                  />
                  {secondaryImagePath && (
                    <Image
                      alt=""
                      src={secondaryImagePath}
                      width={1}
                      height={1}
                      loading="lazy"
                      fetchpriority="low"
                      data-testid="productBoxImageSecondary"
                      className="max-w-full opacity-0 absolute inset-0 w-full h-auto rounded transition ease-in-out duration-300 lz:hover:opacity-100 hover:z-10"
                    />
                  )}
                </span>
                {(showBadge ||
                  product.groupingAttributes.isVirtual ||
                  hasVariationLabel) && (
                  <span className="flex items-center justify-center py-1 font-semibold text-xs w-[120%] -ml-[10%]">
                    <BadgeAndOptionText
                      showBadge={showBadge}
                      hasVariations={hasVariations}
                      badgeDisplayText={badgeDisplayText}
                      badgeImagePath={badgeImagePath}
                      groupingAttributes={product.groupingAttributes}
                      variations={
                        accountVariationsForOptionText
                          ? product.variationMembership
                          : undefined
                      }
                      isListView={productListingView !== "grid"}
                    />
                  </span>
                )}
                <Title
                  {...{
                    productListingView,
                    descriptionHtml,
                    link,
                    oneToFiveStarRating,
                    hasVariationLabel,
                    hasVirtualGrouping: product.groupingAttributes.isVirtual,
                    showBadge,
                  }}
                />
              </a>
            </ProductBoxGridViewWrapper>
            {showRibbonStack && (
              <div
                className={`${previouslyPurchased ? "top-7-1/2" : "top-4"}
            ${"mr-px -mt-px -ml-px overflow-hidden absolute left-0"}`}
              >
                {" "}
                {isWebstaurantPlusEligible && !isRequestAQuoteEnabled && (
                  <PlusLink
                    iconProps={{
                      spriteSheetHref: spritePath,
                      "data-testid": "itemPlus",
                    }}
                    linkProps={{
                      className: "hidden",
                      rel: "",
                    }}
                  />
                )}
                {isNewProduct && (
                  <Ribbon
                    data-testid="itemNew"
                    className={cls(
                      "text-white text-xs py-0.5 z-[15] table mb-2 mr-1.5 -ml-2 leading-[13px] w-auto pl-3 pr-2 bg-green-500 font-normal text-shadow-200 h-full",
                    )}
                    enhancerClasses={cls(
                      isOnCompareProductsPage && "py-0.5 pl-3 pr-2",
                    )}
                  >
                    New
                  </Ribbon>
                )}
                {shouldShowGbbRibbon && (
                  <GoodBetterBest
                    itemNumber={itemNumber}
                    productListingView={productListingView}
                    qualityRating={qualityRatingMembership}
                    className={cls(
                      "relative z-[15]",
                      isOnCompareProductsPage && "border-separate pt-1",
                    )}
                    ribbonClassName={cls(
                      "pt-[3px]",
                      moreThanOneRibbon ? "mb-2" : "mb-0",
                    )}
                    user={user}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <span className="group table-cell float-left mr-1 relative align-middle xs:m-0 xs:float-none xs:h-28 xs:w-28 lz:h-32 lz:w-32 xl:h-40 xl:w-40">
            <a
              onClick={productBoxAnalyticsOnClick}
              className="block relative xs:m-2"
              href={link}
              aria-hidden="true"
              tabIndex={-1}
            >
              {badgeData && (
                <QuantityCountBadge
                  unitDisplayText={badgeData.unitOfMeasure}
                  quantity={badgeData.unitsPerPackaging ?? 1}
                  isGridView={false}
                />
              )}
              <ProductBoxPrimaryImage
                product={product}
                itemNumber={itemNumber}
                compareProductsStore={compareProductsStore}
                alt={altHtml}
                src={primaryImagePath}
                loading={lazyLoadImages ? "lazy" : "eager"}
              />
              {secondaryImagePath && (
                <ProductBoxSecondaryImage
                  product={product}
                  itemNumber={itemNumber}
                  compareProductsStore={compareProductsStore}
                  alt={altHtml}
                  src={secondaryImagePath}
                />
              )}
            </a>
          </span>
        )}
      </>

      <span
        className={
          productListingView === "grid"
            ? "text-center"
            : "relative leading-5 overflow-hidden align-middle xxs:text-base xs:py-2 xs:mb-2-1/2 xs:table-cell md:pl-2 lz:pl-0 xxl:text-lg"
        }
      >
        {productListingView === "list" ? (
          <p
            className={`flex items-center gap-1 text-xs font-semibold text-blue-900 transition-all ease-in -mb-1 xs:mb-0 mt-1 xs:mt-0 whitespace-nowrap
              ${
                product.groupingAttributes.isVirtual || hasBadge
                  ? "md:mb-0"
                  : "md:mb-2"
              }
              ${
                previouslyPurchased && productListingView === "list"
                  ? "opacity-100 h-4"
                  : "opacity-0 h-0"
              }
            `}
          >
            <Icon name="ri-time-line" size="2xsmall" theme="wss" />
            Last Purchased {previouslyPurchased}
          </p>
        ) : null}

        {productListingView === "list" && (
          <a
            href={link}
            className="pt-2 pb-1 xs:pt-0 xs:pb-2 flex items-center justify-center font-semibold text-xs w-max"
            onClick={(ev) => productBoxVariationsOnClick(ev, showBadge, link)}
          >
            <BadgeAndOptionText
              showBadge={showBadge}
              hasVariations={hasVariations}
              badgeDisplayText={badgeDisplayText}
              badgeImagePath={badgeImagePath}
              groupingAttributes={product.groupingAttributes}
              variations={
                accountVariationsForOptionText
                  ? product.variationMembership
                  : undefined
              }
              isListView={productListingView === "list"}
            />
          </a>
        )}
        {productListingView === "list" ? (
          <Title
            {...{
              productListingView,
              descriptionHtml,
              link,
              oneToFiveStarRating,
              hasVariationLabel,
              hasVirtualGrouping: product.groupingAttributes.isVirtual,
              showBadge,
            }}
          />
        ) : null}

        <div
          className={`flex justify-center items-center ${
            productListingView === "grid" && "mb-3 h-7"
          }`}
        >
          {isWebstaurantPlusEligible && !isRequestAQuoteEnabled && (
            <PlusLink
              iconProps={{
                spriteSheetHref: spritePath,
                "data-testid": "itemPlus",
              }}
              linkProps={{
                className:
                  productListingView === "list"
                    ? "hidden"
                    : "flex items-center mx-1",
                rel: "",
              }}
            />
          )}
          {productListingView === "grid" && (
            <div className="inline-flex items-center">
              <Stars rating={overallRating} size="base" />
            </div>
          )}
        </div>
        <ItemNumber
          {...{
            productListingView,
            selectedProduct,
            product,
            user,
            itemsOnAutoReorder,
            userHasSelectedGroupMember,
          }}
        />
        {showRibbonStack && productListingView === "list" && (
          <div
            className={`${
              previouslyPurchased ? "top-7-1/2" : "top-4"
            } flex pl-[0.15rem]`}
          >
            {" "}
            {isWebstaurantPlusEligible && !isRequestAQuoteEnabled && (
              <PlusLink
                iconProps={{
                  spriteSheetHref: spritePath,
                  "data-testid": "itemPlus",
                }}
                linkProps={{
                  className: "float-left flex mr-1",
                  rel: "",
                }}
              />
            )}
            {isNewProduct && (
              <Ribbon
                data-testid="itemNew"
                className="text-white text-xs py-0.5 leading-[13px] z-[15] table w-auto px-2 mb-2 mr-1.5 bg-green-500 font-normal text-shadow-200 h-full"
              >
                New
              </Ribbon>
            )}
            {shouldShowGbbRibbon && (
              <GoodBetterBest
                itemNumber={itemNumber}
                productListingView={productListingView}
                qualityRating={qualityRatingMembership}
                className={cls(
                  "relative z-[15]",
                  isOnCompareProductsPage && "border-separate pt-1",
                )}
                ribbonClassName={cls(moreThanOneRibbon ? "mb-2" : "mb-0")}
                user={user}
              />
            )}
            {productListingView === "list" && (
              <div className="inline-flex mb-1">
                <Stars rating={overallRating} className="flex" />
              </div>
            )}
          </div>
        )}
      </span>
      <div
        // Pricing will remain here because children depend on it. Remove this once pricing is converted to tailwind.
        className={`pricing relative ${pricingStyles}`}
      >
        <PlatinumPlusExclusive
          user={user}
          customerExclusivity={product.customerExclusivity}
        />
        {selectedProduct.isOutlet && product.outletAttributes.condition && (
          <span
            data-testid="outletCondition"
            data-component="outlet-condition"
            className="bg-gray-100 rounded-full inline-block self-center font-bold text-xs leading-snug my-2 py-1 px-3 text-center text-gray-800"
          >
            {product.outletAttributes.condition}
          </span>
        )}
        {isRequestAQuoteEnabled ? (
          <>
            <p className="font-bold text-xs leading-[13px] m-0">
              Custom Pricing
            </p>
            <p className="font-extrabold text-lg m-0">Made-to-Order</p>
          </>
        ) : (
          <Pricing
            {...{
              product,
              mounted,
              user,
              selectedProduct,
              hasCustomizableProductsOverride,
              userHasSelectedGroupMember,
              productListingView,
              preselectedVgMemberItemNumber,
              customizablePricingExtraClassTwo:
                productListingView === "grid" ? "flex justify-center" : "",
            }}
            setIsQuantityDiscountPopoverOpen={setIsQuantityDiscountPopoverOpen}
          />
        )}
        {hasFreeShipping && !user.hasWebPlus && !isRequestAQuoteEnabled && (
          <div className="mx-auto">
            <Ribbon
              data-testid="itemFreeShipping"
              className="text-white text-xs py-0.5 leading-[13px] bg-blue-800 font-normal text-shadow-200"
            >
              Free Shipping
            </Ribbon>
          </div>
        )}
      </div>
      {isNonExclusiveForPlatinumPlusProduct(customerExclusivity, user) && (
        <AddToCartButton
          {...{
            product,
            user,
            setSelectedProduct,
            selectedProduct,
            hasCustomizableProductsOverride,
            purchasingGroupProps,
            setUserHasSelectedGroupMember,
            isItemAlreadyInCart,
            productListingView,
            discountCode,
            showSaleStockNotice,
            preselectedVgMemberItemNumber,
            isRequestAQuoteEnabled,
            groupMemberSelected:
              userHasSelectedGroupMember || !!preselectedVgMemberItemNumber,
            previouslyPurchased: !!previouslyPurchased,
            isOnCompareProductsPage,
          }}
        />
      )}
      {productListingView === "grid" &&
        showSaleStockNotice &&
        product.saleStock?.saleStockLimit &&
        product.saleStock?.remainingSaleQuantity && (
          <LimitedSaleStockNotice
            saleStockLimit={product.saleStock.saleStockLimit}
            saleStockAvailable={product.saleStock.remainingSaleQuantity}
          />
        )}
    </Container>
  );
};

export const mapStateToProps = (state: RootState, ownProps: Props) => {
  if (!ownProps.mounted) return { user: ownProps.user };

  return { user: state.user };
};

export default connect(mapStateToProps)(ProductBox);
